import { z } from 'zod';
import api from './api';
import { ClaimType } from './constants';
import {
  calculatedOrderLineItemSchema,
  claimLineItemCreateSchema,
  claimLineItemSchema,
  draftClaimLineItemSchema,
  giftCardClaimSchema,
  refundClaimSchema,
  typeGuardFromSchema,
  variantExchangeClaimSchema,
} from './schemas';

type ClaimLineItem = z.infer<typeof claimLineItemSchema>;

type ClaimLineItemCreate = Parameters<
  typeof api.createCrewClaim
>[0]['body']['claimLineItems'][number];

const extractVariantExchangeLineItem = ({
  quantity,
  variant,
}: ClaimLineItem): Pick<ClaimLineItemCreate, 'variantExchangeLineItem'> => ({
  variantExchangeLineItem: variant
    ? {
        quantity,
        idFromPlatform: variant.idFromPlatform,
        price: variant.price,
        imgUrl: variant.imgUrl,
        title: variant.title,
        options: variant.options,
      }
    : undefined,
});

const extractCustomFields = ({
  customFields,
}: ClaimLineItem): ClaimLineItemCreate['customFields'] =>
  Object.entries(customFields).map(([id, field]) => ({
    id: Number(id),
    value:
      field.type === 'Date'
        ? new Date(`${field.value}T00:00:00`).toISOString()
        : field.value,
  }));

export const pendingClaimsToClaimLineItemCreate = (
  claimLineItems: ClaimLineItem[],
): ClaimLineItemCreate[] =>
  claimLineItems.map((claim) =>
    claimLineItemCreateSchema.parse({
      ...claim,
      ...extractVariantExchangeLineItem(claim),
      originalStoreOrderLineItemId: claim.lineItem.id,
      images: claim.assets?.map((asset) => asset.cdnName),
      customFields: extractCustomFields(claim),
    } satisfies ClaimLineItemCreate),
  );

export const pendingClaimLineItemToCalculatedLineItem = ({
  reasonPolicy,
  reasonCustomFields,
  ...claimLineItem
}: ClaimLineItem) =>
  ({
    ...claimLineItem,
    ...extractVariantExchangeLineItem(claimLineItem),
    originalStoreOrderLineItemId: claimLineItem.lineItem.id,
    images: claimLineItem.assets?.map((asset) => asset.cdnName),
    customFields: extractCustomFields(claimLineItem),
    originalStoreOrderLineItem: {
      idFromPlatform: claimLineItem.lineItem.idFromPlatform,
      name: claimLineItem.lineItem.name,
      imgUrl: claimLineItem.lineItem.imgUrl,
      optionsFromPlatform: claimLineItem.lineItem.optionsFromPlatform,
    },
  }) satisfies z.infer<typeof calculatedOrderLineItemSchema>;

export const getClaimReasons = (
  claimType: ClaimType,
  claimLineItem: z.infer<typeof draftClaimLineItemSchema>,
) =>
  claimType === ClaimType.return
    ? claimLineItem.lineItem.return.claimReasons
    : claimLineItem.lineItem.warranty.claimReasons;

export const isLineItemInClaims = (
  linItemId: ClaimLineItem['lineItem']['id'],
  claimLineItems: ClaimLineItem[],
) => claimLineItems.some((claim) => claim.lineItem.id === linItemId);

export const isExchangeClaim = typeGuardFromSchema(variantExchangeClaimSchema);
export const isCreditClaim = typeGuardFromSchema(
  z.union([giftCardClaimSchema, refundClaimSchema]),
);
